// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { loadReorderPad } from "@adl/App/Handlers/ReorderPad/ReorderPad";
import { CustomAppState } from "@adl/App/Store/Reducers";
import ReorderPadDateRange from "@adl/App/Widgets/ReorderPad/ReorderPadDateRange";
import ReorderPadPagination from "@adl/App/Widgets/ReorderPad/ReorderPadPagination";
import ReorderPadProductCount from "@adl/App/Widgets/ReorderPad/ReorderPadProductCount";
import ReorderPadProductList from "@adl/App/Widgets/ReorderPad/ReorderPadProductList";
import ReorderPadSearchWithin from "@adl/App/Widgets/ReorderPad/ReorderPadSearchWithin";
import ReorderPadSortSelect from "@adl/App/Widgets/ReorderPad/ReorderPadSortSelect";
import ReorderPadViewSelect from "@adl/App/Widgets/ReorderPad/ReorderPadViewSelect";
import { FormFieldStyle } from "@adl/MobiusOverrides/FormField/FormField";
import { ModalPresentationProps } from "@adl/MobiusOverrides/Modal/Modal";
import ProductCompareFlyOut from "@adl/Overrides/Components/ProductCompareFlyOut";
import { color, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import qs from "qs";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const REORDER_PRODUCTS_ID = "ReorderPad_Products";
interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isLoading: state.xReorderPad.isLoading,
        filterQuery: state.xReorderPad.filterQuery,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadReorderPad,
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadStyles {
    container?: GridContainerProps;
    columnStyles?: {
        leftColumnGridItem?: GridItemProps;
        rightColumnGridItem?: GridItemProps;
    };
    mainSectionStyles?: {
        wrapper?: InjectableCss;
        actionsContainer?: InjectableCss;
    };
    mobileStyles?: {
        wrapper?: InjectableCss;
        contentsWrapper?: InjectableCss;
        productListWrapper?: InjectableCss;
    };
    modal?: ModalPresentationProps;
    filterText?: TypographyPresentationProps;
    filterWrapper?: GridItemProps;
    productCountWrapper?: GridItemProps;
    sortSection?: GridItemProps;
}

export const reorderPadStyles: ReorderPadStyles = {
    container: {
        css: css`
            display: flex;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    columnStyles: {
        leftColumnGridItem: {
            css: css`
                min-width: 300px;
                width: 300px;
                margin-right: 11px;
                ${xScreen("lte", "sm")} {
                    margin-right: 0;
                }
            `,
        },
        rightColumnGridItem: {
            css: css`
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                margin-left: 11px;
                overflow-x: auto;
                ${xScreen("lte", "sm")} {
                    margin-left: 0;
                    margin-top: 16px;
                }
            `,
        },
    },
    mainSectionStyles: {
        wrapper: {
            css: css`
                width: 100%;
            `,
        },
        actionsContainer: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                padding: 12px 0 0;
                border-top: 1px solid ${color("common.border")};
                ${xScreen("lte", 600)} {
                    flex-direction: column;
                    align-items: flex-start;
                    > div:not(:last-child) {
                        margin-bottom: 8px;
                    }
                }
            `,
        },
    },
    mobileStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        contentsWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                & > .top-section {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-between;
                    border-bottom: 1px solid ${color("common.border")};
                    padding-bottom: 24px;
                    ${TypographyStyle} {
                        margin-bottom: 0;
                    }
                    ${xScreen("lte", "md")} {
                        padding-bottom: 12px;
                    }
                }
            `,
        },
    },
    modal: {
        cssOverrides: {
            modalContainer: css`
                ${xScreen("lte", "xs")} {
                    > div {
                        height: 100%;
                        max-height: 100%;
                        margin: 0;
                    }
                }
            `,
        },
    },
    sortSection: {
        css: css`
            padding: 12px;
            border: 1px solid ${color("common.border")};
            width: 100%;
            margin: 10px 0;
            display: flex;
            justify-content: space-between;
                > div {
                    margin-top: 0;
                    width: calc(50% - 8px);
                    span {
                        width: 100%;
                    }
                    &${FormFieldStyle} {
                            padding: 0;
                            border: none;
                            label {
                                font-weight: normal;
                            }
                    }
                    ${xScreen("lte", "xs")} {
                        width: calc(50% - 12px);
                    }
                }
            }
        `,
    },
    filterWrapper: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            & > div {
                width: 100%;
            }
        `,
    },
    productCountWrapper: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            &&& > div[class^="FormFieldStyle"] {
                flex-direction: column;
                align-items: baseline;
                width: 100%;
                border: 1px solid ${color("common.border")};
            }
        `,
    },
    filterText: {
        variant: "h6",
        css: css`
            background: ${color("common.ui9")};
            margin-bottom: 12px;
            padding: 12px 12px 10px;
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
};

const styles = reorderPadStyles;

const ReorderPad: FC<Props> = ({ filterQuery, isLoading, location, loadReorderPad, updateProductInfo, id }) => {
    const history = useHistory();
    const [firstLoad, setFirstLoad] = useState(false);

    const loadProducts = (queryString?: string) => {
        const parsedQuery = queryString
            ? qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString)
            : undefined;
        const params = parsedQuery ? qs.parse(parsedQuery) : {};
        loadReorderPad({
            id: REORDER_PRODUCTS_ID,
            ...params,
            onSuccess: result => {
                if (!firstLoad) {
                    setFirstLoad(true);
                }
            },
        });
    };

    useEffect(() => {
        loadProducts(filterQuery || location.search);
    }, []);

    useEffect(() => {
        if (isLoading || !firstLoad) {
            return;
        }

        const { pathname, search } = location;
        const currentUrl = `${pathname}${search}`;
        const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
        if (newUrl !== currentUrl) {
            history.push(newUrl);
            loadProducts(filterQuery);
        }
    }, [filterQuery]);

    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const shellContext = useShellContext();

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    if (isTabletScreen) {
        return (
            <>
                <ProductCompareFlyOut />
                <StyledWrapper {...styles.mobileStyles?.wrapper}>
                    <StyledWrapper {...styles.mobileStyles?.contentsWrapper}>
                        <StyledWrapper className="top-section">
                            <ReorderPadProductCount />
                            <Button variant="secondary" sizeVariant="small" onClick={modalOpenHandler}>
                                {translate("Sort & Filter")}
                            </Button>
                        </StyledWrapper>
                        <StyledWrapper {...styles.mobileStyles?.productListWrapper}>
                            <ReorderPadProductList />
                        </StyledWrapper>
                    </StyledWrapper>
                    <Modal
                        {...styles.modal}
                        headline={translate("Sort & Filter")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        <ReorderPadProductCount />
                        <StyledWrapper {...styles.sortSection}>
                            <ReorderPadSortSelect />
                            <ReorderPadPagination showResultsPerPage={true} />
                        </StyledWrapper>
                        <GridContainer gap={8}>
                            <GridItem {...styles.filterWrapper}>
                                <ReorderPadSearchWithin />
                                <ReorderPadDateRange />
                            </GridItem>
                        </GridContainer>
                    </Modal>
                </StyledWrapper>
            </>
        );
    }
    return (
        <>
            <ProductCompareFlyOut />
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.columnStyles?.leftColumnGridItem}>
                    <Typography {...styles.filterText} data-test-selector="productListFilterList">
                        {translate("Refine Results")}
                    </Typography>
                    <ReorderPadSearchWithin />
                    <ReorderPadDateRange />
                </StyledWrapper>
                <StyledWrapper {...styles.columnStyles?.rightColumnGridItem}>
                    <StyledWrapper {...styles.mainSectionStyles?.wrapper}>
                        <ReorderPadProductCount />
                        <StyledWrapper {...styles.mainSectionStyles?.actionsContainer}>
                            <ReorderPadSortSelect />
                            <ReorderPadViewSelect />
                            <ReorderPadPagination showResultsPerPage={true} />
                            <ReorderPadPagination showPagesNumber={true} />
                        </StyledWrapper>
                    </StyledWrapper>
                    <ReorderPadProductList />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPad),
    definition: {
        group: "ReorderPad" as any,
    },
};

export default widgetModule;
